import { z } from "zod";

// API Response Schema for parsing server responses
export const StudentAPIResponseSchema = z.object({
	STT_InGiayBao: z.number().nullable(),
	SoGiayBao: z.string().nullable(),
	MaSV: z.string(),
	SBD: z.string().nullable(),
	HoTen: z.string().nullable(),
	NgaySinh: z
		.string()
		.nullable()
		.transform((val) => (val ? new Date(val) : null)),
	Phai: z.string().nullable(),
	CMND: z.string().nullable(),
	Email: z.string().nullable(),
	DienThoai: z.string().nullable(),
	KV: z.string().nullable(),
	DT: z.string().nullable(),
	MaNganh: z.string().nullable(),
	TenNganh: z.string().nullable(),
	NV: z.string().nullable(),
	MaTH: z.string().nullable(),
	DM1: z.number().nullable(),
	DM2: z.number().nullable(),
	DM3: z.number().nullable(),
	TO: z.string().nullable(),
	DTB_Toan_3Nam: z.number().nullable(),
	Toan_HB: z.string().nullable(),
	VatLy_HB: z.string().nullable(),
	Diem_DGNL: z.number().nullable(),
	Diem_TC: z.number().nullable(),
	Diem_UT: z.number().nullable(),
	Diem_TT: z.number().nullable(),
	HoKhau: z.string().nullable(),
	HK_MaTinh: z.string().nullable(),
	HK_TenTinh: z.string().nullable(),
	HK_MaHuyen: z.string().nullable(),
	HK_TenHuyen: z.string().nullable(),
	HK_TenXa: z.string().nullable(),
	Truong_THPT_Lop10: z.string().nullable(),
	Truong_THPT_Lop11: z.string().nullable(),
	Truong_THPT_Lop12: z.string().nullable(),
	NamTN: z.number().nullable(),
	KQ_HocTap: z.string().nullable(),
	KQ_RenLuyen: z.string().nullable(),
	Diem_Lop10: z.string().nullable(),
	Diem_Lop11: z.string().nullable(),
	Diem_Lop12: z.string().nullable(),
	DiemThi: z.string().nullable(),
	DoatGiai: z.string().nullable(),
	CC_AVQT: z.string().nullable(),
	DTT: z.string().nullable(),
	DienTT: z.string().nullable(),
	ThoiGian: z.string().nullable(),
	DiaDiem: z.string().nullable(),
	TT_UuTien: z.string().nullable(),
	FeeStatus: z.string().nullable(),
	createdAt: z.string().transform((val) => new Date(val)),
	updatedAt: z.string().transform((val) => new Date(val)),
	updateInfoAt: z.string().transform((val) => new Date(val)),
	// Nullable: một sinh viên vừa nhập từ file, chưa từng được tiếp nhận, không
	// có ngày tiếp nhận nào để trả về (xem be-express prisma/schema.prisma).
	updatePapersAt: z
		.string()
		.nullable()
		.transform((val) => (val ? new Date(val) : null)),
	DiemCong: z.number().nullable(),
	GiayTo: z.string().nullable(),
	DaTiepNhan: z.boolean(),
	LastUpdateUserName: z.string().nullable(),
	Note: z.string().nullable(),
});

// API response wrapper schema
export const StudentAPIWrapperSchema = z.object({
	success: z.boolean(),
	message: z.string(),
	data: StudentAPIResponseSchema,
});

// Students list API response schema
export const StudentsListAPISchema = z.object({
	success: z.boolean(),
	message: z.string(),
	data: z.object({
		students: z.array(StudentAPIResponseSchema),
		pagination: z.object({
			page: z.number(),
			limit: z.number(),
			total: z.number(),
			totalPages: z.number(),
		}),
	}),
});

export const StudentParamsSchema = z.object({
	mssv: z.string().min(1, "MSSV is required"),
});

// Student type for UI compatibility with existing mock data structure
export interface Student {
	so_giay_bao: string;
	mssv: string;
	ho_ten: string;
	ngay_sinh: Date | null;
	gioi_tinh: string;
	cccd: string;
	email: string;
	khu_vuc: string | null;
	doi_tuong: string | null;
	sdt: string;
	dia_chi: string;
	tinh_thanh: string;
	quan_huyen: string;
	phuong_xa: string;
	truong_thpt: string;
	truong_thpt_lop_10: string | null;
	truong_thpt_lop_11: string | null;
	truong_thpt_lop_12: string;
	ma_nganh: string;
	ten_nganh: string;
	diem_tong: number;
	diem_mon_1: number;
	diem_mon_2: number;
	diem_mon_3: number;
	diem_uu_tien: number;
	diem_cong: number;
	dien_trung_tuyen: string;
	tinh_trang_hoc_phi: string;
	trang_thai_duyet: "pending" | "approved";
	nguoi_duyet: string | null;
	ngay_duyet: Date | null;
	ho_so_da_nhan: string[];
	ghi_chu: string | null;
	trung_tuyen_uu_tien: string | null;
	diem_thi: string | null;
	ma_to_hop: string | null;
	diem_truoc_cong: number;
	chi_tiet_dien_trung_tuyen: string | null;
	giai_thuong: string | null;
	diem_lop_10: string | null;
	diem_lop_11: string | null;
	diem_lop_12: string | null;
	chung_chi: string | null;
	nam_tot_nghiep: number;
	kq_hoctap: string | null;
	/** Đọc thẳng từ cột `Toan_HB` của file upload — chuỗi mô tả điểm theo từng năm
	 * (ví dụ "Toán L10: 9.80; Toán L11: 9.90; Toán L12: 9.90"), giữ nguyên văn, không
	 * tách số. Optional để không phá factory test hiện có; `transformAPIStudentToUIStudent`
	 * luôn đặt giá trị hoặc `null`. */
	toan_hb?: string | null;
	/** Đọc thẳng từ cột `VatLy_HB` của file upload — cùng dạng chuỗi như `toan_hb`. */
	vat_ly_hb?: string | null;
}

// Transform API response to match UI expectations (compatible with mock data structure)
export const transformAPIStudentToUIStudent = (apiStudent: StudentAPIResponse): Student => {
	return {
		so_giay_bao: apiStudent.SoGiayBao || "",
		mssv: apiStudent.MaSV || "",
		ho_ten: apiStudent.HoTen || "",
		ngay_sinh: apiStudent.NgaySinh || null,
		gioi_tinh: apiStudent.Phai || "",
		cccd: apiStudent.CMND || "",
		email: apiStudent.Email || "",
		sdt: apiStudent.DienThoai || "",
		dia_chi: apiStudent.HoKhau || "",
		khu_vuc: apiStudent.KV || null,
		doi_tuong: apiStudent.DT || null,
		tinh_thanh: apiStudent.HK_TenTinh || "",
		quan_huyen: apiStudent.HK_TenHuyen || "",
		phuong_xa: apiStudent.HK_TenXa || "",
		truong_thpt: apiStudent.Truong_THPT_Lop12 || "Không có thông tin",
		truong_thpt_lop_10: apiStudent.Truong_THPT_Lop10 || null,
		truong_thpt_lop_11: apiStudent.Truong_THPT_Lop11 || null,
		truong_thpt_lop_12: apiStudent.Truong_THPT_Lop12 || "Không có thông tin",
		ma_nganh: apiStudent.MaNganh || "Không có thông tin",
		ten_nganh: apiStudent.TenNganh || "Không có thông tin",
		diem_tong: apiStudent.Diem_TT || 0,
		diem_mon_1: apiStudent.DM1 || 0,
		diem_mon_2: apiStudent.DM2 || 0,
		diem_mon_3: apiStudent.DM3 || 0,
		diem_uu_tien: apiStudent.Diem_UT || 0,
		diem_cong: apiStudent.DiemCong || 0,
		toan_hb: apiStudent.Toan_HB ?? null,
		vat_ly_hb: apiStudent.VatLy_HB ?? null,
		diem_thi: apiStudent.DiemThi ? String(apiStudent.DiemThi).replace(/;/g, ",") : null,
		tinh_trang_hoc_phi: apiStudent.FeeStatus || "Chưa có dữ liệu",
		trang_thai_duyet: apiStudent.DaTiepNhan ? "approved" : "pending",
		nguoi_duyet: apiStudent.LastUpdateUserName || null,
		ngay_duyet: apiStudent.updatePapersAt || null,
		dien_trung_tuyen: apiStudent.DTT || "Chưa rõ",
		chi_tiet_dien_trung_tuyen: apiStudent.DienTT?.replace(/2024/g, "").replace(/2025/g, "") || "-",
		ho_so_da_nhan: apiStudent.GiayTo ? apiStudent.GiayTo.split(",").map((item) => item.trim()) : [],
		ghi_chu: apiStudent.Note || null,
		trung_tuyen_uu_tien: apiStudent.TT_UuTien || null,
		ma_to_hop: apiStudent.MaTH || null,
		diem_truoc_cong: apiStudent.Diem_TC || 0,
		giai_thuong: apiStudent.DoatGiai || null,
		diem_lop_10: apiStudent.Diem_Lop10 || null,
		diem_lop_11: apiStudent.Diem_Lop11 || null,
		diem_lop_12: apiStudent.Diem_Lop12?.replace(/;/g, ",") || null,
		chung_chi: apiStudent.CC_AVQT || null,
		nam_tot_nghiep: apiStudent.NamTN || 2025,
		kq_hoctap: apiStudent.KQ_HocTap || null,
	};
};

export type StudentAPIResponse = z.infer<typeof StudentAPIResponseSchema>;
export type StudentAPIWrapper = z.infer<typeof StudentAPIWrapperSchema>;
export type StudentsListAPI = z.infer<typeof StudentsListAPISchema>;
export type StudentParams = z.infer<typeof StudentParamsSchema>;
